import './wdyr'; // Need to be the first import!!
import './index.css';
import 'core-js/stable';
import 'regenerator-runtime/runtime';
import * as Sentry from '@sentry/react';
import React from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, createRoutesFromChildren, matchRoutes, useLocation, useNavigationType } from 'react-router-dom';
import App from './App';
import { ChartProvider, DEFAULT_CHART_HOURS } from './context/ChartContext';
import { DialogProvider } from './context/DialogContext';
import { GroupProvider } from './context/GroupContext';
import { GroupTotalProvider } from './context/GroupTotalContext';
import { UserProvider } from './context/UserContext';
import { NavigationProvider } from 'context/NavigationContext';
import { GroupListProvider } from 'context/GroupListContext';
import { captureConsoleIntegration } from '@sentry/integrations';
import { UserTotalProvider } from 'context/UserTotalContext';
import { RankedpTotalProvider } from 'context/RankedTotalContext';

// only run Sentry in production
// this is when no process is defined
// eslint-disable-next-line
// @ts-ignore
const isProduction = process.env.APP_MODE === 'production';
// eslint-disable-next-line
// @ts-ignore
const dsn = process.env.SENTRY_DSN;

if (isProduction && dsn) {
  Sentry.init({
    dsn,
    integrations: [
      captureConsoleIntegration({ levels: ['error', 'warn'] }),
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect: React.useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
      }),
    ],
    // Performance Monitoring
    tracesSampleRate: 1.0, //  Capture 100% of the transactions
    // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
    tracePropagationTargets: ['localhost', /^https:\/\/app\.solar-daytopper\.com\/api/, /^https:\/\/api\.solar-daytopper\.com/],
    // // Session Replay
    // replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    // replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
  });
}

const container = document.getElementById('root');

// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
const root = createRoot(container!);

root.render(
  <React.StrictMode>
    <BrowserRouter>
      <NavigationProvider>
        <UserProvider>
          <DialogProvider dialogsItems={[]}>
            <ChartProvider loading={false} hours={DEFAULT_CHART_HOURS}>
              <UserTotalProvider cached={{}}>
                <RankedpTotalProvider cached={undefined}>
                  <GroupTotalProvider cached={{}}>
                    <GroupProvider cachedGroups={{}}>
                      <GroupListProvider cachedGroupList={undefined}>
                        <App />
                      </GroupListProvider>
                    </GroupProvider>
                  </GroupTotalProvider>
                </RankedpTotalProvider>
              </UserTotalProvider>
            </ChartProvider>
          </DialogProvider>
        </UserProvider>
      </NavigationProvider>
    </BrowserRouter>
  </React.StrictMode>,
);
